import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';

import { createFormField } from '../../../models/FormField';
import FullProduct from '../../../models/product/FullProduct';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import CartStore from '../../../store/CartStore';
import Paths from '../../../types/Paths';
import ProductClass from '../../../types/ProductClass';
import generatePath from '../../../util/generatePath';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import SelectField from '../../form/SelectField';
import {
  buildStructureSelectionState,
  getCartProductQuantities,
  getStructureRowIssueState,
  getInitialStructureSelectionState,
  getPropertyDefinitions,
  getStructureStockIssues,
  getStructureItems,
  getStructureRowId,
  getStructureRowProduct,
  getStructureRowRequestedQuantity,
  itemHasChildren,
  itemIsAvailable,
  persistStructureSelection,
  propertyValueIsSelectable,
  structureItemBlocksBundle,
  structureRowIsValid,
} from './structureSelectionState';
import { Alert } from 'reactstrap';

const FORM_NAME = 'ProductStructureSelector';
const VALIDATION_MESSAGE_ID = 'ProductStructureSelector__validation';

const STRUCTURE_MESSAGES = defineMessages({
  title: {
    id: 'product.structure.title',
    defaultMessage: 'Configure items',
  },
  includedShort: {
    id: 'product.structure.included.short',
    defaultMessage: '{count} pcs',
  },
  total: {
    id: 'product.structure.total',
    defaultMessage: 'total {count} pcs',
  },
  legendWithQty: {
    id: 'product.structure.legend.with_qty',
    defaultMessage: '{label} ({count} pcs/pkg)',
  },
  fixedWithQty: {
    id: 'product.structure.fixed.with_qty',
    defaultMessage: '{name} ({count} pcs/pkg)',
  },
  validation: {
    id: 'product.structure.validation',
    defaultMessage: 'Please select required items.',
  },
  optionUnavailable: {
    id: 'product.structure.option_unavailable',
    defaultMessage: '{value} (Unavailable)',
  },
  unavailable: {
    id: 'product.structure.unavailable',
    defaultMessage: 'This item is unavailable.',
  },
  stockWarning: {
    id: 'product.structure.stock_warning',
    defaultMessage:
      '{item}: only {available} in stock, but {requested} required for this quantity.',
  },
  stockMaxBundles: {
    id: 'product.structure.stock_max_bundles',
    defaultMessage: 'Maximum {max} packages with current selections.',
  },
  stockValidation: {
    id: 'product.structure.stock_validation',
    defaultMessage:
      'Selected items do not have enough stock for this quantity.',
  },
  unavailableValidation: {
    id: 'product.structure.unavailable_validation',
    defaultMessage: 'Some required items are unavailable.',
  },
  stockSummaryTitle: {
    id: 'product.structure.stock_summary_title',
    defaultMessage:
      "These items don't have enough stock for {quantity} packages:",
  },
  availabilityInfoTitle: {
    id: 'product.structure.availability_info_title',
    defaultMessage:
      'These structure items are not available for the selected quantity:',
  },
  availabilityInfoItem: {
    id: 'product.structure.availability_info_item',
    defaultMessage: '{item} (available {available}, required {requested})',
  },
  availabilityInfoUnavailable: {
    id: 'product.structure.availability_info_unavailable',
    defaultMessage: '{item} (unavailable)',
  },
});

/** Display name for fixed (no-variant) structure rows: label + product name (+ props). */
const buildFixedRowDisplayName = (item) => {
  if (!item) {
    return '';
  }
  const props =
    (item.properties || []).map((p) => `${p.name} ${p.value}`).join(', ') || '';
  const namePart = props ? `${item.name} – ${props}` : item.name;
  if (item.label) {
    return `${item.label}, ${namePart}`;
  }
  return namePart;
};

const getStructureItemProductPath = (item, routeService) => {
  const productId = item?.products_id;
  if (!productId) {
    return null;
  }

  const slug = item.slug || productId;
  const path = generatePath(Paths.Product, {
    slug,
    id: productId,
  });

  return routeService ? routeService.getPath(path) : path;
};

const ProductStructureSelector = observer(({
  product,
  quantity,
  onChange,
  intl,
  cartStore,
  routeService,
}) => {
  // Read cart during render so MobX tracks nested bundle_products updates.
  const cartProductQuantities = getCartProductQuantities(
    cartStore?.cart?.products
  );
  const cartProductQuantitiesKey = Object.keys(cartProductQuantities)
    .sort()
    .map((productId) => `${productId}:${cartProductQuantities[productId]}`)
    .join('|');

  // SSR-safe defaults only — do not read sessionStorage during initial render.
  // Restore persisted selections in useEffect after mount / product change.
  const [selectionState, setSelectionState] = useState(() => {
    const initialState = getInitialStructureSelectionState(
      product,
      quantity,
      getCartProductQuantities(cartStore?.cart?.products),
      {}
    );

    return {
      mapping: initialState.mapping,
      propertySelections: initialState.propertySelections,
    };
  });
  const onChangeRef = useRef(onChange);
  // Skip persisting once per product id so the first empty SSR-safe state
  // cannot overwrite session before the restore effect applies.
  const persistedForProductIdRef = useRef(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const structureItems = useMemo(() => {
    return getStructureItems(product);
  }, [product]);

  const fieldsByCellKey = useMemo(() => {
    const map = new Map();
    structureItems.forEach((item) => {
      if (!itemHasChildren(item)) {
        return;
      }
      const rowId = getStructureRowId(item);
      getPropertyDefinitions(item).forEach((propDef) => {
        map.set(`${rowId}-${propDef.id}`, createFormField({ value: '' }));
      });
    });
    return map;
  }, [structureItems]);

  useEffect(() => {
    const initialState = getInitialStructureSelectionState(
      product,
      quantity,
      getCartProductQuantities(cartStore?.cart?.products)
    );

    setSelectionState({
      mapping: initialState.mapping,
      propertySelections: initialState.propertySelections,
    });
  }, [product]);

  useEffect(() => {
    setSelectionState((prev) =>
      buildStructureSelectionState({
        product,
        propertySelections: prev.propertySelections,
        bundleQuantity: quantity,
        cartProductQuantities: getCartProductQuantities(
          cartStore?.cart?.products
        ),
      })
    );
  }, [product, quantity, cartProductQuantitiesKey, cartStore]);

  const { mapping, propertySelections } = selectionState;

  useEffect(() => {
    structureItems.forEach((item) => {
      if (!itemHasChildren(item)) {
        return;
      }

      const rowId = getStructureRowId(item);
      getPropertyDefinitions(item).forEach((propDef) => {
        const field = fieldsByCellKey.get(`${rowId}-${propDef.id}`);
        if (!field) {
          return;
        }

        const nextValue = String(
          propertySelections?.[rowId]?.[propDef.id] || ''
        );
        if (String(field.value || '') !== nextValue) {
          field.setValue(nextValue);
        }
      });
    });
  }, [fieldsByCellKey, propertySelections, structureItems]);

  useEffect(() => {
    if (
      !product?.id ||
      product.class !== ProductClass.STRUCTURE ||
      structureItems.length === 0
    ) {
      return;
    }

    if (persistedForProductIdRef.current !== product.id) {
      persistedForProductIdRef.current = product.id;
      return;
    }

    persistStructureSelection(product.id, {
      propertySelections,
      quantity,
    });
  }, [product?.id, product?.class, propertySelections, quantity, structureItems.length]);

  const isValid = useMemo(() => {
    return structureItems.every(
      (item) =>
        !structureItemBlocksBundle({
          product,
          item,
          mapping,
          requestedQuantity: getStructureRowRequestedQuantity({
            item,
            bundleQuantity: quantity,
          }),
          cartProductQuantities,
        })
    );
  }, [cartProductQuantities, mapping, product, quantity, structureItems]);

  const stockIssuesState = useMemo(() => {
    return getStructureStockIssues({
      product,
      mapping,
      bundleQuantity: quantity,
      cartProductQuantities,
    });
  }, [cartProductQuantities, mapping, product, quantity]);

  const rowIssuesById = stockIssuesState.issuesByRowId;
  const uniqueBlockingIssues = useMemo(
    () => Array.from(new Set(Object.values(rowIssuesById))),
    [rowIssuesById]
  );
  const stockSummaryIssues = useMemo(
    () => uniqueBlockingIssues.filter((issue) => issue?.type === 'stock'),
    [uniqueBlockingIssues]
  );
  const hasUnavailableIssue = useMemo(
    () => uniqueBlockingIssues.some((issue) => issue?.type === 'unavailable'),
    [uniqueBlockingIssues]
  );
  const unavailableIssues = useMemo(
    () =>
      uniqueBlockingIssues.filter((issue) => issue?.type === 'unavailable'),
    [uniqueBlockingIssues]
  );
  const showAvailabilityInfo =
    stockSummaryIssues.length > 0 || unavailableIssues.length > 0;

  const getAvailabilityInfoItemName = (issue) => {
    if (issue.selectedProduct?.name) {
      return issue.selectedProduct.name;
    }

    if (issue.item?.label || issue.item?.name) {
      return issue.item.label || issue.item.name;
    }

    const rowId = issue.rowId || (issue.rowIds && issue.rowIds[0]);
    if (rowId) {
      const structureItem = structureItems.find(
        (item) => getStructureRowId(item) === String(rowId)
      );
      if (structureItem) {
        return structureItem.label || structureItem.name;
      }
    }

    return issue.childProductId || rowId || '';
  };

  const availabilityMaxBundles = useMemo(() => {
    const maxValues = stockSummaryIssues
      .map((issue) => issue.maxBundles)
      .filter((value) => Number.isFinite(value));

    return maxValues.length > 0 ? Math.min(...maxValues) : null;
  }, [stockSummaryIssues]);

  const showValidationMessage = useMemo(() => {
    if (isValid) {
      return false;
    }

    return structureItems.some((item) => {
      const bundleBlocked = structureItemBlocksBundle({
        product,
        item,
        mapping,
        requestedQuantity: getStructureRowRequestedQuantity({
          item,
          bundleQuantity: quantity,
        }),
        cartProductQuantities,
      });

      return bundleBlocked;
    });
  }, [
    cartProductQuantities,
    isValid,
    mapping,
    product,
    quantity,
    structureItems,
  ]);

  useEffect(() => {
    const notify = onChangeRef.current;
    if (notify) {
      notify(mapping, isValid);
    }
  }, [mapping, structureItems, isValid]);

  if (product.class !== ProductClass.STRUCTURE || structureItems.length === 0) {
    return null;
  }

  const handlePropertyChange = (parentId, propId, value, item) => {
    setSelectionState((prev) => {
      const prevForItem = prev.propertySelections[parentId] || {};
      const nextForItem = {
        ...prevForItem,
        [propId]: value || undefined,
      };
      const propertyDefs = getPropertyDefinitions(item);
      const normalizedForItem = propertyDefs.reduce((selections, propDef) => {
        const selectedValue = selections[propDef.id];
        if (!selectedValue || String(propDef.id) === String(propId)) {
          return selections;
        }

        if (
          propertyValueIsSelectable({
            item,
            selectionMap: selections,
            propId: propDef.id,
            value: selectedValue,
          })
        ) {
          return selections;
        }

        return {
          ...selections,
          [propDef.id]: undefined,
        };
      }, nextForItem);
      const nextPropertySelections = {
        ...prev.propertySelections,
        [parentId]: normalizedForItem,
      };
      return buildStructureSelectionState({
        product,
        propertySelections: nextPropertySelections,
        prioritizedRowId: parentId,
        bundleQuantity: quantity,
        cartProductQuantities,
      });
    });
  };

  return (
    <div
      className={classNames('ProductStructureSelector', {
        'ProductStructureSelector--invalid': !isValid,
      })}
    >
      <div className="ProductStructureSelector__title">
        <FormattedMessage {...STRUCTURE_MESSAGES.title} />
      </div>
      <ul className="ProductStructureSelector__list">
        {structureItems.map((item, index) => {
          const rowId = getStructureRowId(item);
          const hasChildren = itemHasChildren(item);
          const isAvailable = itemIsAvailable(item);
          const perPackQty = Number(item.quantity) || 1;
          const totalQty = getStructureRowRequestedQuantity({
            item,
            bundleQuantity: quantity,
          });
          const propertyDefs = hasChildren ? getPropertyDefinitions(item) : [];
          const rowIsValid = structureRowIsValid({
            product,
            item,
            mapping,
            requestedQuantity: totalQty,
            cartProductQuantities,
          });
          const selectedRowProduct = getStructureRowProduct({
            item,
            mapping,
          });
          const rowIssueState =
            rowIssuesById[rowId] ||
            getStructureRowIssueState({
              product,
              item,
              mapping,
              requestedQuantity: totalQty,
              cartProductQuantities,
            });
          const fixedDisplayName = buildFixedRowDisplayName(item);
          const itemProductPath = getStructureItemProductPath(
            item,
            routeService
          );
          const fixedProductPath = !hasChildren ? itemProductPath : null;
          const rowMessageId = `ProductStructureSelector__row-message-${rowId}`;
          const hasUnavailableMessage = rowIssueState?.type === 'unavailable';
          const hasStockMessage = rowIssueState?.type === 'stock';
          const describedById = !rowIsValid
            ? hasUnavailableMessage
              ? rowMessageId
              : hasStockMessage
              ? rowMessageId
              : VALIDATION_MESSAGE_ID
            : undefined;
          const rowSelections = propertySelections[rowId] || {};

          return (
            <li
              key={rowId || `${item.products_id}-${index}`}
              className={classNames('ProductStructureSelector__row', {
                'ProductStructureSelector__row--invalid': !rowIsValid,
                'ProductStructureSelector__row--unavailable': !isAvailable,
              })}
            >
              <div className="ProductStructureSelector__row-surface">
                <span
                  className={classNames('ProductStructureSelector__status', {
                    'ProductStructureSelector__status--valid': rowIsValid,
                    'ProductStructureSelector__status--invalid': !rowIsValid,
                  })}
                  aria-hidden="true"
                />
                <fieldset
                  className={classNames('ProductStructureSelector__pill', {
                    'ProductStructureSelector__pill--fixed': !hasChildren,
                    'ProductStructureSelector__pill--unavailable': !isAvailable,
                  })}
                  aria-describedby={describedById}
                  aria-errormessage={!rowIsValid ? describedById : undefined}
                  aria-disabled={!isAvailable}
                  aria-invalid={!rowIsValid ? true : undefined}
                >
                  {!hasChildren && (
                    <legend className="sr-only">
                      {perPackQty > 1 ? (
                        <FormattedMessage
                          {...STRUCTURE_MESSAGES.fixedWithQty}
                          values={{
                            name: fixedDisplayName,
                            count: perPackQty,
                          }}
                        />
                      ) : (
                        fixedDisplayName
                      )}
                    </legend>
                  )}
                  {hasChildren && (
                    <legend className="ProductStructureSelector__legend">
                      {itemProductPath ? (
                        <MaybeExternalLink
                          className="ProductStructureSelector__legend-link"
                          to={itemProductPath}
                        >
                          {perPackQty > 1 ? (
                            <FormattedMessage
                              {...STRUCTURE_MESSAGES.legendWithQty}
                              values={{ label: item.label, count: perPackQty }}
                            />
                          ) : (
                            item.label
                          )}
                        </MaybeExternalLink>
                      ) : perPackQty > 1 ? (
                        <FormattedMessage
                          {...STRUCTURE_MESSAGES.legendWithQty}
                          values={{ label: item.label, count: perPackQty }}
                        />
                      ) : (
                        item.label
                      )}
                    </legend>
                  )}
                  <div className="ProductStructureSelector__body">
                    <div className="ProductStructureSelector__body-main">
                      {hasChildren ? (
                        <div className="ProductStructureSelector__matrix">
                          {propertyDefs.map((propDef) => {
                            const cellKey = `${rowId}-${propDef.id}`;
                            const field = fieldsByCellKey.get(cellKey);
                            const optionStateForValue = (val) => {
                              const isSelectable = propertyValueIsSelectable({
                                item,
                                selectionMap: rowSelections,
                                propId: propDef.id,
                                value: val,
                              });

                              return {
                                isSelectable,
                              };
                            };
                            const options = [
                              { value: '', label: '' },
                              ...propDef.values.map((val) => {
                                const optionState = optionStateForValue(val);

                                return {
                                  disabled: !optionState.isSelectable,
                                  value: String(val),
                                  label: optionState.isSelectable
                                    ? String(val)
                                    : intl.formatMessage(
                                        STRUCTURE_MESSAGES.optionUnavailable,
                                        {
                                          value: String(val),
                                        }
                                      ),
                                };
                              }),
                            ];
                            return (
                              <div
                                className="ProductStructureSelector__matrix-cell"
                                key={cellKey}
                              >
                                {field ? (
                                  <SelectField
                                    aria-describedby={describedById}
                                    aria-errormessage={
                                      !rowIsValid ? describedById : undefined
                                    }
                                    aria-invalid={
                                      !rowIsValid ? true : undefined
                                    }
                                    className="ProductStructureSelector__select"
                                    disabled={!isAvailable}
                                    field={field}
                                    fieldName={`structure_${rowId}_${propDef.id}`}
                                    floatingLabel
                                    formName={FORM_NAME}
                                    invalid={!rowIsValid}
                                    label={propDef.name}
                                    options={options}
                                    value={rowSelections[propDef.id] || ''}
                                    onChange={(event) =>
                                      handlePropertyChange(
                                        rowId,
                                        propDef.id,
                                        event.target.value,
                                        item
                                      )
                                    }
                                  />
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : fixedProductPath ? (
                        <MaybeExternalLink
                          className="ProductStructureSelector__fixed-name"
                          to={fixedProductPath}
                        >
                          {perPackQty > 1 ? (
                            <FormattedMessage
                              {...STRUCTURE_MESSAGES.fixedWithQty}
                              values={{
                                name: fixedDisplayName,
                                count: perPackQty,
                              }}
                            />
                          ) : (
                            fixedDisplayName
                          )}
                        </MaybeExternalLink>
                      ) : (
                        <span className="ProductStructureSelector__fixed-name">
                          {perPackQty > 1 ? (
                            <FormattedMessage
                              {...STRUCTURE_MESSAGES.fixedWithQty}
                              values={{
                                name: fixedDisplayName,
                                count: perPackQty,
                              }}
                            />
                          ) : (
                            fixedDisplayName
                          )}
                        </span>
                      )}
                    </div>
                    {quantity > 1 && (
                      <div className="ProductStructureSelector__total-wrap">
                        <FormattedMessage
                          {...STRUCTURE_MESSAGES.total}
                          values={{ count: totalQty }}
                        />
                      </div>
                    )}
                    {hasUnavailableMessage && (
                      <Alert
                        id={rowMessageId}
                        className={classNames(
                          'ProductStructureSelector__row-message',
                          'ProductStructureSelector__row-message--danger'
                        )}
                        color="danger"
                        role="alert"
                      >
                        <FormattedMessage {...STRUCTURE_MESSAGES.unavailable} />
                      </Alert>
                    )}
                    {!hasUnavailableMessage && hasStockMessage && (
                      <Alert
                        id={rowMessageId}
                        className={classNames(
                          'ProductStructureSelector__row-message',
                          'ProductStructureSelector__row-message--danger'
                        )}
                        color="danger"
                        role="alert"
                      >
                        <div>
                          <FormattedMessage
                            {...STRUCTURE_MESSAGES.stockWarning}
                            values={{
                              available: rowIssueState?.available ?? 0,
                              item:
                                rowIssueState?.selectedProduct?.name ||
                                selectedRowProduct?.name ||
                                item.label ||
                                item.name,
                              requested: rowIssueState?.requested ?? totalQty,
                            }}
                          />
                        </div>
                        {Number.isFinite(rowIssueState?.maxBundles) && (
                          <div>
                            <FormattedMessage
                              {...STRUCTURE_MESSAGES.stockMaxBundles}
                              values={{ max: rowIssueState.maxBundles }}
                            />
                          </div>
                        )}
                      </Alert>
                    )}
                  </div>
                </fieldset>
              </div>
            </li>
          );
        })}
      </ul>
      {showAvailabilityInfo && (
        <div
          className="ProductStructureSelector__availability-info"
          role="status"
        >
          <div className="ProductStructureSelector__availability-info-title">
            <FormattedMessage {...STRUCTURE_MESSAGES.availabilityInfoTitle} />
          </div>
          <ul className="ProductStructureSelector__availability-info-list">
            {stockSummaryIssues.map((issue) => (
              <li key={`stock-${issue.childProductId || issue.rowId}`}>
                <FormattedMessage
                  {...STRUCTURE_MESSAGES.availabilityInfoItem}
                  values={{
                    item: getAvailabilityInfoItemName(issue),
                    available: issue.available ?? 0,
                    requested: issue.requested ?? 0,
                  }}
                />
              </li>
            ))}
            {unavailableIssues.map((issue) => (
              <li key={`unavailable-${issue.rowId || issue.item?.products_id}`}>
                <FormattedMessage
                  {...STRUCTURE_MESSAGES.availabilityInfoUnavailable}
                  values={{
                    item: getAvailabilityInfoItemName(issue),
                  }}
                />
              </li>
            ))}
          </ul>
          {Number.isFinite(availabilityMaxBundles) && (
            <div className="ProductStructureSelector__availability-info-max">
              <FormattedMessage
                {...STRUCTURE_MESSAGES.stockMaxBundles}
                values={{
                  max: availabilityMaxBundles,
                }}
              />
            </div>
          )}
        </div>
      )}
      {showValidationMessage && (
        <div
          id={VALIDATION_MESSAGE_ID}
          className="ProductStructureSelector__validation"
          role="alert"
        >
          <FormattedMessage
            {...(stockSummaryIssues.length > 0
              ? STRUCTURE_MESSAGES.stockValidation
              : hasUnavailableIssue
              ? STRUCTURE_MESSAGES.unavailableValidation
              : STRUCTURE_MESSAGES.validation)}
          />
        </div>
      )}
    </div>
  );
});

ProductStructureSelector.propTypes = {
  product: modelOf(FullProduct).isRequired,
  quantity: PropTypes.number,
  onChange: PropTypes.func,
  intl: intlShape.isRequired,
  cartStore: modelOf(CartStore),
  routeService: PropTypes.instanceOf(RouteService).isRequired,
};

ProductStructureSelector.defaultProps = {
  quantity: 1,
};

export { getInitialStructureSelectionState };

export default inject(
  'cartStore',
  'routeService'
)(injectIntl(ProductStructureSelector));
